<template>
  <div>
    <div class="container-fluid">
      <div class="page-header">
        <Breadcrumbs main="account" title="subscription" />
      </div>

      <!-- Abonnement courant -->
      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header">
              <h5>Current subscription</h5>
            </div>
            <div class="card-body">
              <div v-if="superadmin" class="alert alert-secondary mb-0">
                You are signed in as the super-admin: full access, no
                subscription required.
              </div>
              <div v-else-if="subscription">
                <div class="row">
                  <div class="col-md-4">
                    <p class="mb-1 text-muted">Plan</p>
                    <h6>{{ currentPlanName || subscription.planId || '—' }}</h6>
                  </div>
                  <div class="col-md-3">
                    <p class="mb-1 text-muted">Status</p>
                    <h6 :class="statusClass">{{ subscription.status }}</h6>
                  </div>
                  <div class="col-md-3">
                    <p class="mb-1 text-muted">Provider</p>
                    <h6 class="text-capitalize">{{ subscription.provider }}</h6>
                  </div>
                  <div class="col-md-2">
                    <p class="mb-1 text-muted">Renews</p>
                    <h6>{{ periodEndLabel }}</h6>
                  </div>
                </div>
                <div class="mt-3">
                  <button
                    v-if="subscription.provider === 'stripe' && subscription.providerCustomerId"
                    class="btn btn-outline-primary mr-2"
                    @click="openPortal"
                    :disabled="busy"
                  >
                    Billing portal
                  </button>
                  <button
                    v-if="!subscription.cancelAtPeriodEnd && subscription.status !== 'canceled'"
                    class="btn btn-outline-danger"
                    @click="cancelSubscription"
                    :disabled="busy"
                  >
                    Cancel subscription
                  </button>
                  <span
                    v-if="subscription.cancelAtPeriodEnd"
                    class="text-warning ml-2"
                  >
                    Scheduled to cancel at the end of the period.
                  </span>
                </div>
              </div>
              <p v-else class="text-muted mb-0">
                No subscription yet. Choose a plan below to get started.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Plans -->
      <div class="row">
        <div class="col-md-4" v-for="plan in plans" :key="plan.id">
          <div class="card pricing-card" :class="{ 'border-primary': isCurrentPlan(plan) }">
            <div class="card-header text-center">
              <h5 class="mb-0">{{ plan.name }}</h5>
              <span
                v-if="isCurrentPlan(plan)"
                class="badge badge-primary"
              >Current plan</span>
            </div>
            <div class="card-body text-center">
              <h3 class="price">
                {{ formatPrice(plan) }}
                <small class="text-muted">/mo</small>
              </h3>
              <p class="text-muted">
                {{ plan.maxStores === -1 ? 'Unlimited stores' : (plan.maxStores + ' store' + (plan.maxStores > 1 ? 's' : '')) }}
              </p>
              <ul class="list-unstyled features">
                <li v-for="(f, i) in plan.features" :key="i">
                  <feather type="check" style="width:14px;height:14px"></feather>
                  {{ f }}
                </li>
              </ul>
            </div>
            <div class="card-footer" v-if="!superadmin">
              <button
                class="btn btn-primary btn-block mb-2"
                @click="checkoutStripe(plan)"
                :disabled="busy || !plan.stripePriceId"
                :title="plan.stripePriceId ? '' : 'Stripe price not configured'"
              >
                Subscribe with Stripe
              </button>
              <button
                class="btn btn-outline-primary btn-block"
                @click="checkoutPaypal(plan)"
                :disabled="busy || !plan.paypalPlanId"
                :title="plan.paypalPlanId ? '' : 'PayPal plan not configured'"
              >
                Subscribe with PayPal
              </button>
            </div>
          </div>
        </div>
        <div class="col-md-12" v-if="!plans.length && !loading">
          <div class="alert alert-warning">
            No plan is available yet. Plans are seeded by the API
            (<code>tools/seed-accounts.js</code>).
          </div>
        </div>
      </div>

      <!-- Historique -->
      <div class="row" v-if="events.length">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header">
              <h5>History</h5>
            </div>
            <div class="card-body">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Provider</th>
                    <th>Event</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ev in events" :key="ev.id">
                    <td>{{ formatDate(ev.createdAt) }}</td>
                    <td class="text-capitalize">{{ ev.provider }}</td>
                    <td>{{ ev.eventType }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "@/utils/firestore-shim";
import UserAuth from "../../auth/index.js";

export default {
  name: "AccountSubscription",
  data() {
    return {
      plans: [],
      subscription: null,
      entitlement: null,
      events: [],
      loading: true,
      busy: false,
      superadmin: false
    };
  },
  computed: {
    currentPlanName() {
      return (this.entitlement && this.entitlement.planName) || "";
    },
    statusClass() {
      const s = this.subscription ? this.subscription.status : "";
      if (s === "active" || s === "trialing") return "text-success";
      if (s === "past_due") return "text-warning";
      if (s === "canceled") return "text-danger";
      return "text-muted";
    },
    periodEndLabel() {
      if (!this.subscription || !this.subscription.currentPeriodEnd) return "—";
      return this.formatDate(this.subscription.currentPeriodEnd);
    }
  },
  async mounted() {
    await this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const [plansRes, subRes, profile] = await Promise.all([
          firebase.api.get("/billing/plans"),
          firebase.api.get("/billing/subscription"),
          firebase.auth().fetchProfile()
        ]);
        UserAuth.setProfile(profile);
        this.plans = (plansRes && plansRes.plans) || [];
        this.subscription = (subRes && subRes.subscription) || null;
        this.events = (subRes && subRes.events) || [];
        this.entitlement = (profile && profile.entitlement) || null;
        this.superadmin = Boolean(this.entitlement && this.entitlement.superadmin);
      } catch (e) {
        this.toast("Could not load billing data: " + e.message, "error");
      } finally {
        this.loading = false;
      }
    },
    isCurrentPlan(plan) {
      return Boolean(this.subscription && this.subscription.planId === plan.id);
    },
    formatPrice(plan) {
      const currency = String(plan.currency || "usd").toUpperCase();
      const symbol = currency === "USD" ? "$" : currency === "EUR" ? "€" : currency + " ";
      return symbol + Number(plan.priceMonthly || 0).toFixed(2);
    },
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
    },
    async checkoutStripe(plan) {
      this.busy = true;
      try {
        const res = await firebase.api.post("/billing/checkout/stripe", {
          planId: plan.id
        });
        if (res && res.url) {
          window.location.assign(res.url);
          return;
        }
        this.toast("Stripe did not return a payment URL.", "error");
      } catch (e) {
        this.toast(e.message, "error");
      } finally {
        this.busy = false;
      }
    },
    async checkoutPaypal(plan) {
      this.busy = true;
      try {
        const res = await firebase.api.post("/billing/checkout/paypal", {
          planId: plan.id
        });
        if (res && res.approveUrl) {
          window.location.assign(res.approveUrl);
          return;
        }
        this.toast("PayPal did not return an approval URL.", "error");
      } catch (e) {
        this.toast(e.message, "error");
      } finally {
        this.busy = false;
      }
    },
    async openPortal() {
      this.busy = true;
      try {
        const res = await firebase.api.post("/billing/portal", {});
        if (res && res.url) {
          window.location.assign(res.url);
          return;
        }
        this.toast("Billing portal unavailable.", "error");
      } catch (e) {
        this.toast(e.message, "error");
      } finally {
        this.busy = false;
      }
    },
    async cancelSubscription() {
      if (!window.confirm("Cancel your subscription? You keep access until the end of the period.")) {
        return;
      }
      this.busy = true;
      try {
        await firebase.api.post("/billing/cancel", {});
        this.toast("Subscription scheduled for cancellation.", "success");
        await this.load();
      } catch (e) {
        this.toast(e.message, "error");
      } finally {
        this.busy = false;
      }
    },
    toast(message, type) {
      if (this.$toasted) {
        this.$toasted.show(message, {
          theme: "bubble",
          position: "bottom-right",
          type: type || "info",
          duration: 3000
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.pricing-card {
  height: 100%;
  .price {
    font-weight: 700;
  }
  .features li {
    padding: 4px 0;
  }
}
</style>
